/**
 * Component: Form Field Script
 * -----------------------------------------------------------------------------
 * Scripts applied to input fields within forms.
 *
 */
import classes from '~/config/classes'

/**
  * DOM selectors.
  */
const selectors = {
  textInput: '[type="text"], [type="email"], [type="tel"], [type="password"], [type="number"]',
  formGroupField: '.form-group__field',
  inputButton: 'js-input-button',
  selectInputs: '.form-group__field select',
}

/**
  * Create a new example object.
  */
export default () => {

  /**
    * DOM node selectors.
    */
  const nodeSelectors = {
    textInputs: [...document.querySelectorAll(selectors.textInput)],
    selectInputs: [...document.querySelectorAll(selectors.selectInputs)],
  }

  /**
    * Initialise component.
    */
  function init() {
    setEventListeners()
  }

  /**
   * Set event listeners.
   */
  function setEventListeners() {

    /**
     * Text inputs.
     */
    if (nodeSelectors.textInputs.length) {
      const events = ['blur', 'focus', 'input']

      nodeSelectors.textInputs.forEach(input => {
        const groupField = input.closest(selectors.formGroupField)

        if (groupField) {
          if (input.value.trim().length) {
            groupField.classList.remove(classes.blurred)
            groupField.classList.add(classes.active)
            input.classList.add(classes.active)
          } else {
            groupField.classList.add(classes.empty)
          }
        }

        events.forEach(eventType => {
          input.addEventListener(eventType, (event) => {
            handleInputUpdate(event, input)
          })
        })

        setInterval(() => {
          if (input.value.trim().length) {
            handleInputUpdate({ type: 'input', value: input.value }, input)
          }
        }, 1000)
      })
    }

    /**
     * Select inputs.
     */
    if (nodeSelectors.selectInputs.length) {
      nodeSelectors.selectInputs.forEach(select => {
        select.addEventListener('input', (event) => {
          handleSelectChange(event)
        })
      })
    }
  }

  /**
   * Deals with the select field changing.
   * @param {Object} event - Event object from event handler.
   */
  function handleSelectChange(event) {
    if (!event?.target?.value) {
      return
    }

    const parent = event.target.closest(selectors.formGroupField)
    if (!parent) {
      return
    }

    const label = parent.querySelector('label')
    if (!label) {
      return
    }

    label.classList.add('visually-hidden')
  }

  /**
   * Handles the input fields updating.
   * @param {Object} event - Event object from event handler.
   * @param {Object} input - HTMLElement object.
   */
  function handleInputUpdate(event, input) {
    const groupField = input.closest('.form-group__field')

    if (groupField) {
      if (input.value.trim().length) {
        groupField.classList.remove(classes.empty)
      } else {
        groupField.classList.add(classes.empty)
      }
    }

    if (event.type === 'blur') {
      if (groupField) {
        groupField.classList.add(classes.blurred)
      }
    }

    if (event?.target?.value || event.type === 'focus' || event.type === 'input') {
      input.classList.add(classes.active)

      if (groupField) {
        groupField.classList.remove(classes.blurred)
        groupField.classList.add(classes.active)
      }
      updateLabelState(input)
      return
    }

    input.classList.remove(classes.active)
    updateLabelState(input, 'remove')
    if (groupField) {
      groupField.classList.remove(classes.active)
    }
  }

  /**
   * Updates the input label state.
   * @param {Object} input - HTML element.
   * @param {String} [state] - Whether the label is being added or removed.
   */
  function updateLabelState(input, state = 'add') {
    if (!input.hasAttribute(selectors.inputButton)) {
      return
    }

    const parent = input.closest(selectors.formGroupField)
    const label = parent?.querySelector('label')

    if (!label) {
      return
    }

    if (!input?.dataset?.originalLabel) {
      input.dataset.originalLabel = label.textContent
    }

    if (state === 'add') {
      const newButton = document.createElement('button')

      newButton.textContent = input?.dataset?.buttonLabel || label.textContent
      newButton.classList.add('form-group__label--button')
      newButton.type = 'submit'
      label.innerHTML = ''
      label.insertAdjacentElement('afterbegin', newButton)
      return
    }

    const oldButton = parent.querySelector('button')
    if (!oldButton) {
      return
    }

    label.textContent = input.dataset.originalLabel
    oldButton.remove()
  }

  /**
    * Expose public interface.
    */
  return Object.freeze({
    init,
    handleInputUpdate,
  })
}
